import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, BookOpen, Filter, X, Calendar, Award, Clock, Star, ChevronDown, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { BookItem } from '../components/Elze/BookItem';
import { books, authors } from '../data/books';
// Type definitions
type ExploreTab = 'authors' | 'works';
type WorkStatus = 'all' | 'published' | 'unpublished' | 'in-progress' | 'in-transfer';
type SortOption = 'recent' | 'alphabetical' | 'popular';
type AuthorType = 'all' | 'fiction' | 'nonfiction' | 'poetry';
export function ExplorePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ExploreTab>('works');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // Filters for works
  const [statusFilter, setStatusFilter] = useState<WorkStatus>('all');
  const [sortOption, setSortOption] = useState<SortOption>('recent');
  const [genreFilter, setGenreFilter] = useState<string>('all');
  // Filters for authors
  const [authorTypeFilter, setAuthorTypeFilter] = useState<AuthorType>('all');
  const [authorSortOption, setAuthorSortOption] = useState<SortOption>('popular');
  // Extract all unique genres from works
  const allGenres = ['all', ...new Set(books.map(work => work.genre))];
  // Filter works based on selected criteria
  const filteredWorks = books.filter(work => {
    // Filter by status
    if (statusFilter !== 'all' && work.status !== statusFilter) return false;
    // Filter by genre
    if (genreFilter !== 'all' && work.genre !== genreFilter) return false;
    // Filter by search query (title or author)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return work.title.toLowerCase().includes(query) || work.author.name.toLowerCase().includes(query);
    }
    return true;
  });
  // Sort works
  const sortedWorks = [...filteredWorks].sort((a, b) => {
    if (sortOption === 'recent') {
      return parseInt(b.year) - parseInt(a.year);
    } else if (sortOption === 'alphabetical') {
      return a.title.localeCompare(b.title);
    } else if (sortOption === 'popular') {
      return (b.readers || 0) - (a.readers || 0);
    }
    return 0;
  });
  // Filter authors
  const filteredAuthors = authors.filter(author => {
    // Filter by type
    if (authorTypeFilter !== 'all' && author.type !== authorTypeFilter) return false;
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return author.name.toLowerCase().includes(query) || author.bio.toLowerCase().includes(query);
    }
    return true;
  });
  // Sort authors
  const sortedAuthors = [...filteredAuthors].sort((a, b) => {
    if (authorSortOption === 'recent') {
      return b.id.localeCompare(a.id); // Simplification, would use a date in reality
    } else if (authorSortOption === 'alphabetical') {
      return a.name.localeCompare(b.name);
    } else if (authorSortOption === 'popular') {
      return (b.followers || 0) - (a.followers || 0);
    }
    return 0;
  });
  // Handle click on a work - standardized navigation
  const handleWorkClick = (workId: string) => {
    navigate(`/book/${workId}`);
  };
  // Handle click on an author
  const handleAuthorClick = (authorId: string) => {
    navigate(`/profile/${authorId}`);
  };
  // Function to get color for genre tag
  const getGenreColor = genre => {
    const genreColors = {
      Fantasia: 'bg-purple-100 text-purple-800',
      Avventura: 'bg-green-100 text-green-800',
      Romanzo: 'bg-blue-100 text-blue-800',
      Drammatico: 'bg-yellow-100 text-yellow-800',
      Saggistica: 'bg-gray-100 text-gray-800',
      Scienza: 'bg-cyan-100 text-cyan-800',
      Divulgazione: 'bg-teal-100 text-teal-800',
      Poesia: 'bg-pink-100 text-pink-800',
      Lirica: 'bg-rose-100 text-rose-800',
      Thriller: 'bg-red-100 text-red-800',
      Giallo: 'bg-amber-100 text-amber-800',
      Noir: 'bg-slate-100 text-slate-800',
      Biografia: 'bg-orange-100 text-orange-800',
      Storia: 'bg-indigo-100 text-indigo-800',
      Storico: 'bg-violet-100 text-violet-800',
      Haiku: 'bg-fuchsia-100 text-fuchsia-800',
      'Versi liberi': 'bg-lime-100 text-lime-800',
      Spirituale: 'bg-sky-100 text-sky-800',
      'Sviluppo personale': 'bg-emerald-100 text-emerald-800',
      Narrativa: 'bg-blue-100 text-blue-800',
      Saggi: 'bg-gray-100 text-gray-800'
    };
    return genreColors[genre] || 'bg-gray-100 text-gray-800';
  };
  // Translation of work status
  const getStatusLabel = status => {
    switch (status) {
      case 'published':
        return 'Published';
      case 'unpublished':
        return 'Unpublished';
      case 'in-progress':
        return 'In Progress';
      case 'in-transfer':
        return 'In Transfer';
      default:
        return status;
    }
  };
  // Badge color based on status
  const getStatusBadgeColor = status => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'unpublished':
        return 'bg-yellow-100 text-yellow-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'in-transfer':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="w-full bg-gray-50 min-h-screen" data-id="element-613">
      {/* Header */}
      <div className="mb-4 sm:mb-6 md:mb-8" data-id="element-614">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-615">
          <div data-id="element-616">
            <h1 className="text-2xl md:text-3xl font-bold text-[#164149]" data-id="element-617">
              Explore
            </h1>
            <p className="text-sm text-gray-600 mt-2" data-id="element-618">
              Discover new authors and works available on the platform
            </p>
          </div>
          {/* Search bar */}
          <div className="relative w-full sm:w-auto sm:min-w-[300px]" data-id="element-619">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-620">
              <Search size={18} className="text-gray-400" data-id="element-621" />
            </div>
            <input type="text" placeholder={activeTab === 'works' ? 'Search works or authors...' : 'Search authors...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#161c2d]/20 focus:border-[#161c2d] text-sm" data-id="element-622" />
            {searchQuery && <button className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setSearchQuery('')} data-id="element-623">
                <X size={16} className="text-gray-400 hover:text-gray-600" data-id="element-624" />
              </button>}
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="mb-6" data-id="element-625">
        <div className="flex border-b border-gray-200" data-id="element-626">
          <button onClick={() => setActiveTab('works')} className={`py-3 px-4 border-b-2 font-medium text-sm flex items-center ${activeTab === 'works' ? 'border-[#C6D6AB] text-[#164149]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-627">
            <BookOpen size={18} className="mr-2" data-id="element-628" />
            Works
          </button>
          <button onClick={() => setActiveTab('authors')} className={`py-3 px-4 border-b-2 font-medium text-sm flex items-center ${activeTab === 'authors' ? 'border-[#C6D6AB] text-[#164149]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-629">
            <Users size={18} className="mr-2" data-id="element-630" />
            Authors
          </button>
          {/* Filters button */}
          <div className="ml-auto" data-id="element-631">
            <button onClick={() => setShowFilters(!showFilters)} className="py-3 px-4 text-sm font-medium text-gray-700 flex items-center hover:text-[#161c2d]" data-id="element-632">
              <Filter size={18} className="mr-2" data-id="element-633" />
              Filters
              <ChevronDown size={16} className={`ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} data-id="element-634" />
            </button>
          </div>
        </div>
      </div>
      {/* Filters */}
      {showFilters && <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-635">
          {activeTab === 'works' ? <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-636">
              {/* Status filter */}
              <div data-id="element-637">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-638">
                  Status
                </label>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as WorkStatus)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#161c2d]/20 focus:border-[#161c2d] text-sm" data-id="element-639">
                  <option value="all" data-id="element-640">All statuses</option>
                  <option value="published" data-id="element-641">Published</option>
                  <option value="unpublished" data-id="element-642">Unpublished</option>
                  <option value="in-progress" data-id="element-643">In Progress</option>
                  <option value="in-transfer" data-id="element-644">In Transfer</option>
                </select>
              </div>
              {/* Genre filter */}
              <div data-id="element-645">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-646">
                  Genre
                </label>
                <select value={genreFilter} onChange={e => setGenreFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#161c2d]/20 focus:border-[#161c2d] text-sm" data-id="element-647">
                  <option value="all" data-id="element-648">All genres</option>
                  {allGenres.filter(g => g !== 'all').map(genre => <option key={genre} value={genre} data-id="element-649">
                        {genre}
                      </option>)}
                </select>
              </div>
              {/* Sort options */}
              <div data-id="element-650">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-651">
                  Sort by
                </label>
                <select value={sortOption} onChange={e => setSortOption(e.target.value as SortOption)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#161c2d]/20 focus:border-[#161c2d] text-sm" data-id="element-652">
                  <option value="recent" data-id="element-653">Most recent</option>
                  <option value="alphabetical" data-id="element-654">Alphabetical</option>
                  <option value="popular" data-id="element-655">Most popular</option>
                </select>
              </div>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-656">
              {/* Author type filter */}
              <div data-id="element-657">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-658">
                  Type
                </label>
                <select value={authorTypeFilter} onChange={e => setAuthorTypeFilter(e.target.value as AuthorType)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#161c2d]/20 focus:border-[#161c2d] text-sm" data-id="element-659">
                  <option value="all" data-id="element-660">All types</option>
                  <option value="fiction" data-id="element-661">Fiction</option>
                  <option value="nonfiction" data-id="element-662">Non-fiction</option>
                  <option value="poetry" data-id="element-663">Poetry</option>
                </select>
              </div>
              {/* Sort options for authors */}
              <div data-id="element-664">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-665">
                  Sort by
                </label>
                <select value={authorSortOption} onChange={e => setAuthorSortOption(e.target.value as SortOption)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#161c2d]/20 focus:border-[#161c2d] text-sm" data-id="element-666">
                  <option value="popular" data-id="element-667">Most followed</option>
                  <option value="alphabetical" data-id="element-668">Alphabetical</option>
                  <option value="recent" data-id="element-669">Most recent</option>
                </select>
              </div>
            </div>}
        </div>}
      {/* Results - Works */}
      {activeTab === 'works' && <>
          {sortedWorks.length > 0 ? <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6" data-id="element-670">
              {sortedWorks.map(work => <div key={work.id} className="w-full cursor-pointer" data-id="element-671">
                  <div className="relative" data-id="element-672">
                    <BookItem cover={work.cover} title={work.title} genre={work.genre} year={work.year} authorName={work.author.name} authorImage={work.author.image} id={work.id} onClick={() => handleWorkClick(work.id)} data-id="element-673" />
                    {/* Status badge */}
                    <div className="absolute top-2 right-2 z-10" data-id="element-674">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadgeColor(work.status)}`} data-id="element-675">
                        {getStatusLabel(work.status)}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div> : <div className="text-center py-12 bg-white rounded-lg border border-gray-200 shadow-sm" data-id="element-676">
              <Filter size={48} className="mx-auto text-gray-400 mb-4" data-id="element-677" />
              <h3 className="text-lg font-medium text-[#164149] mb-2" data-id="element-678">
                No works found
              </h3>
              <p className="text-sm text-gray-600" data-id="element-679">
                No works match your selected search criteria.
              </p>
            </div>}
        </>}
      {/* Results - Authors */}
      {activeTab === 'authors' && <>
          {sortedAuthors.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-680">
              {sortedAuthors.map(author => <div key={author.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleAuthorClick(author.id)} data-id="element-681">
                  <div className="p-4" data-id="element-682">
                    <div className="flex items-start" data-id="element-683">
                      <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0" data-id="element-684">
                        <img src={author.image} alt={author.name} className="w-full h-full object-cover" data-id="element-685" />
                      </div>
                      <div className="ml-4 flex-1" data-id="element-686">
                        <h3 className="text-lg font-semibold text-[#164149] flex items-center" data-id="element-687">
                          {author.name}
                          <ExternalLink size={14} className="ml-2 text-gray-400" data-id="element-688" />
                        </h3>
                        {/* Genre tags */}
                        <div className="flex flex-wrap gap-1 mt-2 mb-3" data-id="element-689">
                          {author.genres && author.genres.map((genre, index) => <span key={index} className={`text-xs px-2 py-0.5 rounded-full ${getGenreColor(genre)}`} data-id="element-690">
                                {genre}
                              </span>)}
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-2" data-id="element-691">
                          {author.bio}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 border-t border-gray-100" data-id="element-692">
                    <div className="flex items-center justify-between text-sm" data-id="element-693">
                      <div className="flex items-center" data-id="element-694">
                        <BookOpen size={14} className="text-gray-500 mr-1" data-id="element-695" />
                        <span className="text-gray-700" data-id="element-696">
                          {author.works} works
                        </span>
                      </div>
                      <span className="text-sm text-blue-600 hover:text-red-600 transition-colors" data-id="element-697">
                        View profile
                      </span>
                    </div>
                  </div>
                </div>)}
            </div> : <div className="text-center py-12 bg-white rounded-lg border border-gray-200 shadow-sm" data-id="element-698">
              <Users size={48} className="mx-auto text-gray-400 mb-4" data-id="element-699" />
              <h3 className="text-lg font-medium text-[#164149] mb-2" data-id="element-700">
                No authors found
              </h3>
              <p className="text-sm text-gray-600" data-id="element-701">
                No authors match your selected search criteria.
              </p>
            </div>}
        </>}
    </div>;
}